var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import BigNumber from 'bignumber.js';
import { getIcon } from './getIcon';
import { getSuiClient } from './client';
import { getTxErrorMessage } from './txResult';
import { getRawErrorMessage, logTransactionError } from './errorMaps';
import { LST } from 'stsui-sdk';
import { LST_PARAMS, SUI_DECIMALS, MIN_SUI_BALANCE, calculateReceiveAmount, EXPLORER_BASE_URL, } from './stsuiConstants';
/**
 * Gas budget for stake/unstake, in MIST. 0.05 SUI — ~4x the heaviest cost measured on this
 * path (12.3M MIST; median 1.4M). Pinned because the wallet's auto-estimate has come in
 * under the real cost on the money path and reverted transactions on-chain with
 * InsufficientGas. Kept small on purpose: staking spends SUI and the budget is reserved out
 * of the same gas coin, so an oversized budget would break near-max stakes. See
 * transactionHandlers.ts for the full note.
 */
const GAS_BUDGET_MIST = 50000000;
// Helper functions
// `digest` lets BalanceContext read the transaction back and update wallet balances from
// its own amounts, instead of waiting for the next balance poll.
const dispatchTransactionEvent = (txType, success, digest) => {
    const eventType = success ? 'transactionSuccess' : 'transactionError';
    document.dispatchEvent(new CustomEvent(eventType, { detail: { txType, success, digest } }));
};
const getTransactionTitle = (status, action) => {
    if (status === 'awaiting')
        return 'Awaiting Response';
    if (status === 'insufficient_gas_balance')
        return 'Insufficient Gas Balance';
    if (status === 'cancelled')
        return 'Transaction Cancelled';
    const titleMap = {
        stake: 'Transaction Successful',
        unstake: 'Transaction Successful',
    };
    if (status === 'success' && action) {
        return titleMap[action];
    }
    if (status === 'error' && action) {
        return action === 'stake' ? 'Stake Failed' : 'Unstake Failed';
    }
    return 'Transaction Failed';
};
const createExplorerLink = (network, digest) => {
    if (!digest)
        return '';
    const networkSuffix = network === 'testnet' ? 'testnet' : 'mainnet';
    return `${EXPLORER_BASE_URL}/${networkSuffix}/tx/${digest}`;
};
const getTransactionDescription = (status, action, displayAmount, errorMessage, stSuiExchangeRate, unstakingFee) => {
    if (status === 'awaiting') {
        const tokenSymbol = action === 'stake' ? 'SUI' : 'stSUI';
        return `Requesting wallet to approve ${displayAmount} ${tokenSymbol} tokens.`;
    }
    if (status === 'insufficient_gas_balance') {
        return `At least ${MIN_SUI_BALANCE} SUI is required in your wallet to cover gas fees.`;
    }
    if (status === 'cancelled') {
        if (action === 'stake') {
            return `Staking of ${displayAmount} SUI into stSUI was cancelled.`;
        }
        else {
            return `Unstaking from ${displayAmount} stSUI into SUI was cancelled.`;
        }
    }
    if (status === 'success' && action && displayAmount) {
        const actionType = action === 'stake' ? 'Stake' : 'Instant Unstake';
        const feeString = unstakingFee === null || unstakingFee === void 0 ? void 0 : unstakingFee.toString();
        const receivedAmount = calculateReceiveAmount(displayAmount, stSuiExchangeRate ? parseFloat(stSuiExchangeRate) : 0, actionType, feeString);
        if (action === 'stake') {
            return `You successfully minted ${receivedAmount} stSUI tokens.`;
        }
        else {
            return `You successfully redeemed ${receivedAmount} SUI tokens.`;
        }
    }
    if (status === 'error' && action) {
        const actionVerb = action === 'stake' ? 'stake' : 'unstake';
        return `Failed to ${actionVerb} ${displayAmount || ''}. ${errorMessage || ''}`;
    }
    return errorMessage || 'Transaction failed';
};
const createNotificationInfo = (network, status, params = {}) => {
    const { result, action, displayAmount, errorMessage, stSuiExchangeRate, unstakingFee } = params;
    const explorerLink = createExplorerLink(network, result === null || result === void 0 ? void 0 : result.digest);
    const notificationConfigs = {
        awaiting: {
            title: getTransactionTitle('awaiting'),
            description: getTransactionDescription('awaiting', action, displayAmount),
            spinner: true,
        },
        success: {
            title: getTransactionTitle('success', action),
            description: getTransactionDescription('success', action, displayAmount, undefined, stSuiExchangeRate, unstakingFee),
            image: getIcon('success'),
            link: explorerLink,
        },
        error: {
            title: getTransactionTitle('error', action),
            description: getTransactionDescription('error', action, displayAmount, errorMessage),
            image: getIcon('failed'),
            link: explorerLink,
        },
        cancelled: {
            title: getTransactionTitle('cancelled', action),
            description: getTransactionDescription('cancelled', action, displayAmount),
            image: getIcon('failed'),
        },
        insufficient_gas_balance: {
            title: getTransactionTitle('insufficient_gas_balance'),
            description: getTransactionDescription('insufficient_gas_balance'),
            image: getIcon('failed'),
        },
    };
    return notificationConfigs[status];
};
export const stsuiTransactionHandler = (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { amount, network, action, address, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setIsTransactionLoading, displayAmount, suiBalance, resetInputValues, stSuiExchangeRate, unstakingFee, } = params;
    const showNotification = (status, notifParams = {}) => {
        setNotificationInfo(createNotificationInfo(network, status, Object.assign(Object.assign({}, notifParams), { stSuiExchangeRate,
            unstakingFee })));
        setIsNotificationOpen(true);
    };
    const handleError = (rawError, shouldResetInputs = true, result, 
    // Stands in for the *message* when the caught value carries none, so callers can forward
    // the value itself unconditionally and keep its payload in the log.
    fallbackMessage = '') => {
        const errorMessage = getRawErrorMessage(rawError) || fallbackMessage;
        let friendlyMessage = errorMessage;
        if (errorMessage.includes('is lower than the needed amount')) {
            friendlyMessage = 'Insufficient balance for transaction';
        }
        else if (errorMessage.includes('User rejected')) {
            friendlyMessage = 'Transaction was cancelled by user';
        }
        logTransactionError(action, rawError, friendlyMessage);
        showNotification('error', {
            errorMessage: friendlyMessage,
            action,
            displayAmount,
            result,
        });
        setIsTransactionLoading(false);
        if (shouldResetInputs)
            resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
        dispatchTransactionEvent(action, false);
    };
    if (!address) {
        console.error('Address is required for stSUI transactions');
        return;
    }
    const suiBalanceDecimal = new BigNumber(suiBalance).dividedBy(new BigNumber(10).pow(SUI_DECIMALS));
    if (suiBalanceDecimal.lt(MIN_SUI_BALANCE)) {
        showNotification('insufficient_gas_balance');
        return;
    }
    const suiClient = getSuiClient(network || 'mainnet');
    showNotification('awaiting', { action, displayAmount });
    setIsTransactionLoading(true);
    try {
        const amountBigNumber = new BigNumber(amount).multipliedBy(new BigNumber(10).pow(SUI_DECIMALS));
        const amountString = amountBigNumber.toFixed(0);
        const lst = new LST(LST_PARAMS);
        let transactionBlock;
        switch (action) {
            case 'stake':
                transactionBlock = yield lst.mint(amountString, address);
                break;
            case 'unstake':
                transactionBlock = yield lst.redeem(amountString, address);
                break;
            default:
                throw new Error(`Unsupported action: ${action}`);
        }
        if (!transactionBlock) {
            throw new Error('Failed to create transaction block');
        }
        transactionBlock.setGasBudget(GAS_BUDGET_MIST);
        signAndExecuteTransaction({ transaction: transactionBlock }, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                try {
                    if (result.success) {
                        showNotification('success', {
                            result,
                            action,
                            displayAmount,
                        });
                        resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
                        // BalanceContext listens for this and refreshes balances from the
                        // transaction, so no separate refetchBalances call is needed here.
                        dispatchTransactionEvent(action, true, result.digest);
                    }
                    else {
                        const txbCheck = yield suiClient
                            .waitForTransaction({ digest: result.digest, include: { effects: true } })
                            .catch(() => null);
                        const errorMessage = (txbCheck && getTxErrorMessage(txbCheck)) || 'Transaction failed';
                        handleError(errorMessage, true, result);
                    }
                }
                catch (error) {
                    console.error('Error waiting for transaction:', error);
                    handleError('Failed to confirm transaction', true, result);
                }
                finally {
                    setIsTransactionLoading(false);
                }
            }),
            onError: (error) => {
                // Hand over the error itself, not its message, so handleError's log keeps the stack.
                handleError(error, true, undefined, 'Transaction cancelled');
            },
        });
    }
    catch (error) {
        handleError(error);
    }
});
